// Offside — delayed-offside with tag-up, per real NHL rules.
//
// Owns:   the rule that flags an attacking team's skater being inside
//         the offensive zone before the puck crosses the blue line,
//         and (if that skater touches the puck) whistles for a faceoff
//         at the nearest neutral-zone dot.
// Reads:  state.puck.pos / .ownerId, state.players.{pos, teamId},
//         tuning.positioning.zones.blueLineX.
// Mutates: state.rules.offside (the pending-offside scratch),
//          state.puck / state.clock (via `enterStoppage` when called).
// Emits:  `Stoppage{ reason: "Offside" }` when the rule fires.
//
// Per real hockey:
//   - The puck must cross the attacking team's blue line BEFORE all
//     attacking skaters' bodies cross it.
//   - If an attacker is already in the OZ when the puck enters, they
//     are "potentially offside" — but play continues until they touch
//     the puck (delayed offside).
//   - If all potentially-offside attackers retreat to the neutral
//     zone ("tag up"), the violation clears and play continues
//     uninterrupted.
//   - If the puck leaves the OZ before any of the above happens, the
//     violation also clears.
//
// Implementation walks the blue-line crossing tick by tick with
// (prevPuckX, currentPuckX). The first attacking-team teammate other
// than the puck-carrier whose body is in the OZ at the crossing tick
// becomes a pending-offside player. Touching the puck (becoming
// `puck.ownerId`) trips the whistle; exiting the OZ clears the entry.
//
// Called from `core/SimTick.ts` Step 16a — after puck integration and
// possession update, before goal detection (so an offside cancels a
// would-be goal). Zero-allocation past the per-tick PlayerId list
// growth.

import type { PlayerId, TeamId } from "@arcade-hockey/shared";
import { nearestFaceoffSpot, neutralZoneSpotsFor } from "../state/faceoff-spots.js";
import { tuning } from "../state/tuning.js";
import type { MatchState } from "../state/types.js";
import { enterStoppage } from "./stoppage.js";

/**
 * Per-tick offside check. Called once from SimTick after the puck has
 * been integrated and possession resolved for this tick.
 *
 * `prevPuckX` is the puck's x at the START of this tick (before
 * integration) — used to detect blue-line crossings within the tick.
 */
export function tickOffside(state: MatchState, prevPuckX: number): void {
  // Offside applies during live Play and Overtime. Stoppage / Faceoff /
  // GoalCelebration / PeriodBreak / Shootout — no enforcement, and any
  // pending state is cleared.
  if (state.clock.phase !== "Play" && state.clock.phase !== "Overtime") {
    state.rules.offside = undefined;
    return;
  }

  const blue = tuning.positioning.zones.blueLineX;
  const puckX = state.puck.pos.x;

  // Resolve any pending offside first — that determines whether we
  // need to whistle, clear, or continue waiting.
  if (state.rules.offside !== undefined) {
    const off = state.rules.offside;
    const inOZ = off.team === "home" ? puckX > blue : puckX < -blue;

    // 1. Puck left the OZ → clear (no violation, play continues).
    if (!inOZ) {
      state.rules.offside = undefined;
      return;
    }

    // 2. Offside skater touched the puck → whistle.
    const owner = state.puck.ownerId;
    if (owner !== null && off.potentialIds.includes(owner)) {
      const spot = nearestFaceoffSpot(
        off.puckEnterX,
        off.puckEnterY,
        neutralZoneSpotsFor(off.team),
      );
      enterStoppage(state, spot, "Offside");
      state.rules.offside = undefined;
      return;
    }

    // 3. All offside skaters tagged up (exited the OZ) → clear.
    let anyStillIn = false;
    for (const pid of off.potentialIds) {
      const p = state.playersById.get(pid);
      if (p === undefined) continue;
      const stillInOZ = off.team === "home" ? p.pos.x > blue : p.pos.x < -blue;
      if (stillInOZ) {
        anyStillIn = true;
        break;
      }
    }
    if (!anyStillIn) {
      state.rules.offside = undefined;
      return;
    }

    // Still pending — fall through.
    return;
  }

  // No pending state — look for a fresh blue-line crossing this tick.
  // The puck entered HOME's offensive zone (their attacking blue line
  // at +12) when prev < blue and curr >= blue.
  const enteredHomeOZ = prevPuckX < blue && puckX >= blue;
  const enteredAwayOZ = prevPuckX > -blue && puckX <= -blue;
  if (!enteredHomeOZ && !enteredAwayOZ) return;

  const attackingTeam: TeamId = enteredHomeOZ ? "home" : "away";
  // Players whose body is currently in the attacking team's OZ AND who
  // are not the puck-carrier (the carrier brought the puck in, so they
  // can't be offside on it).
  const carrierId = state.puck.ownerId;
  const potential: PlayerId[] = [];
  for (const p of state.players) {
    if (p.teamId !== attackingTeam) continue;
    if (p.benched) continue;
    if (p.id === carrierId) continue;
    const inOZ = attackingTeam === "home" ? p.pos.x > blue : p.pos.x < -blue;
    if (inOZ) potential.push(p.id);
  }

  if (potential.length === 0) {
    // Legal entry — no offside players in the zone.
    return;
  }

  state.rules.offside = {
    team: attackingTeam,
    potentialIds: potential,
    puckEnterX: puckX,
    puckEnterY: state.puck.pos.y,
  };
}
